import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { caseScheduleRepository } from './repository';
import { useRealtimeCaseUpdates } from './realtime-hooks';
import { useRealtimeAssetUpdates } from '../asset/realtime-hooks';
import { RealtimeEventType } from '@peritasai/types';
import type { AssetStatusUpdatedEvent, RealtimeEvent } from '@peritasai/types';
import { SurgicalCaseEntity } from './case-schedule.entity';
import type { CasesResponse } from './repository';

/**
 * Whether a case's tracked trays, carts, or unexpected items include the
 * given physical asset id — used to scope an asset-status realtime event to
 * only the case(s) it actually affects, instead of refetching the whole list.
 */
const caseTracksAsset = (
  caseEntity: SurgicalCaseEntity,
  assetId: string
): boolean => {
  const checklist = caseEntity.buildChecklist;
  if (!checklist) return false;

  const groups = [
    ...checklist.getTrays(),
    ...checklist.getAvailableCarts(),
    ...checklist.getUnexpectedItems(),
  ];
  return groups.some((group) =>
    group.items.some((item) => item.assetId === assetId)
  );
};

/**
 * Query keys for case schedule queries
 */
export const caseScheduleKeys = {
  all: ['case-schedule'] as const,
  lists: () => [...caseScheduleKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...caseScheduleKeys.lists(), { filters }] as const,
  infinite: (filters: Record<string, unknown>) =>
    [...caseScheduleKeys.all, 'infinite', { filters }] as const,
  detail: (caseId: string) =>
    [...caseScheduleKeys.all, 'detail', caseId] as const,
  workflowSummary: (targetDate?: string) =>
    [...caseScheduleKeys.all, 'workflow-summary', { targetDate }] as const,
  pickingStations: () => [...caseScheduleKeys.all, 'picking-stations'] as const,
} as const;

/**
 * Options for useCase hook
 */
export interface UseCaseOptions {
  enabled?: boolean;
  /** Whether to enable real-time updates via SSE for this case's tracked assets. */
  enableRealtime?: boolean;
}

/**
 * Hook for fetching a single case with its build checklist, e.g. for the
 * cart build progress screen.
 */
export function useCase(caseId: string | null, options: UseCaseOptions = {}) {
  const { enabled = true, enableRealtime = false } = options;
  const queryClient = useQueryClient();
  const queryKey = caseScheduleKeys.detail(caseId ?? '');

  const query = useQuery({
    queryKey,
    queryFn: () =>
      caseScheduleRepository.getCase(caseId as string, {
        includeChecklistItems: true,
      }),
    enabled: enabled && !!caseId,
  });

  // This case's tray/cart status is computed server-side from its tracked
  // assets, so a case-level event alone never fires when one of those
  // assets changes status elsewhere (e.g. an RFID scan). Without this, the
  // detail screen only ever reflects such a change on next remount —
  // navigating away and back — instead of live (PVS-2631).
  useRealtimeAssetUpdates({
    eventTypes: [RealtimeEventType.ASSET_STATUS_UPDATED],
    enabled: enableRealtime && !!caseId,
    onEvent: (event) => {
      if (event.type !== RealtimeEventType.ASSET_STATUS_UPDATED) return;

      const assetId = (event as AssetStatusUpdatedEvent).data.asset.id;
      const cachedCase = queryClient.getQueryData<SurgicalCaseEntity>(queryKey);
      if (!cachedCase || !caseTracksAsset(cachedCase, assetId)) return;

      void caseScheduleRepository
        .getCase(caseId as string, { includeChecklistItems: true })
        .then((updatedCase) => {
          queryClient.setQueryData(queryKey, updatedCase);
        })
        .catch((error: unknown) => {
          console.error(
            'Failed to refresh case after asset status update',
            error
          );
        });
    },
  });

  // A direct case-field mutation on another device/tab only reaches this
  // screen via CASE_UPDATED — refetch rather than patch from the event
  // payload so we don't clobber fields (like preferenceCard) that getCase()
  // enriches beyond the raw CaseResponseDto.
  useRealtimeCaseUpdates({
    eventTypes: [RealtimeEventType.CASE_UPDATED],
    enabled: enableRealtime && !!caseId,
    onEvent: (event) => {
      if (event.type !== RealtimeEventType.CASE_UPDATED) return;
      if (!caseId) return;

      const updatedCaseDto = event.data.case;
      const cachedCase = queryClient.getQueryData<SurgicalCaseEntity>(queryKey);
      if (!cachedCase) return;
      if (
        updatedCaseDto.id !== cachedCase.id &&
        updatedCaseDto.caseId !== cachedCase.caseHospitalId
      ) {
        return;
      }

      void caseScheduleRepository
        .getCase(caseId, { includeChecklistItems: true })
        .then((updatedCase) => {
          queryClient.setQueryData(queryKey, updatedCase);
        })
        .catch((error: unknown) => {
          console.error('Failed to refresh case after case update', error);
        });
    },
  });

  return query;
}

/**
 * Mutation for releasing a previously reserved tracked item (tray) from a
 * case's cart. Invalidates the case detail query on success so the tray
 * grid reflects the release.
 */
export function useUnassignTrackedItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, assetId }: { caseId: string; assetId: string }) =>
      caseScheduleRepository.unassignTrackedItem(caseId, assetId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: caseScheduleKeys.detail(variables.caseId),
      });
    },
  });
}

export interface ConfirmCaseAssetsVariables {
  caseId: string;
  /** Assets detected in the room that still need assigning to the case. */
  assetIds: string[];
  /** Full name of the confirming user, stored as the case's verifiedByName. */
  verifiedBy: string;
}

/**
 * Mutation for confirming all required assets are present in the case room.
 * Bulk-assigns any newly detected assets to the case first, then verifies.
 * Invalidates the case detail query on success — realtime CASE_UPDATED
 * normally patches the cache first, this is the fallback when realtime is
 * disabled or lagging.
 */
export function useConfirmCaseAssets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      caseId,
      assetIds,
      verifiedBy,
    }: ConfirmCaseAssetsVariables) => {
      if (assetIds.length > 0) {
        await caseScheduleRepository.assignTrackedItemsBatch(caseId, assetIds);
      }
      return caseScheduleRepository.verifyAssets(caseId, verifiedBy);
    },
    onSuccess: (_data, { caseId }) => {
      void queryClient.invalidateQueries({
        queryKey: caseScheduleKeys.detail(caseId),
      });
    },
    onError: (error) => {
      console.error('Failed to verify case assets', error);
    },
  });
}

/**
 * What the case list can be narrowed by. Declared rather than an index-signature
 * bag, so adding a filter is a compiler error everywhere it has to be threaded
 * through — the query and the realtime subscription both read it.
 */
export interface CaseScheduleFilters {
  /** ISO instant; defaults to the start of today. */
  startDate?: string;
  /** ISO instant; defaults to the end of today. */
  endDate?: string;
  status?: string;
  /**
   * Narrows to a single OR when supplied — the tablet day view passes the
   * location the device is configured for. Omitted, the API returns every
   * location, which is what the multi-OR board wants.
   */
  locationId?: string;
}
/**
 * Mutation for marking a case's cart as dispatched (sent to OR). Invalidates
 * all case-schedule list queries on success so the case-cart-assembly queues
 * reflect the new cartCompletenessStatus — realtime SSE normally patches the
 * cache first, this is the fallback when realtime is disabled or lagging.
 */
export function useUpdateDispatchStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ caseId, dispatchStatus }: { caseId: string; dispatchStatus: boolean }) =>
      caseScheduleRepository.updateDispatchStatus(caseId, dispatchStatus),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: caseScheduleKeys.lists(),
      });
    },
    onError: (error) => {
      console.error('Failed to update dispatch status', error);
    },
  });
}

/**
 * Options for useCaseSchedulePaginated hook
 */
export interface UseCaseSchedulePaginatedOptions {
  /** Whether to enable real-time updates via SSE */
  enableRealtime?: boolean;
  /**
   * Whether to fetch at all. Callers that narrow by location pass `false`
   * until one is chosen, so an unscoped request for every OR's cases is never
   * made just to be thrown away.
   */
  enabled?: boolean;
}

/**
 * Whether a case belongs in the list a query with these filters describes.
 *
 * Checked against the case's *current* values, so an event that moved it out
 * of the window answers `false` and the caller drops it. The location check
 * matters as much as the date one: the realtime subscription cannot filter by
 * location (`CaseEventFilters` carries no such field), so every OR's events
 * reach a single-OR day view and only this keeps the others out of its list.
 */
function matchesCaseFilters(
  caseEntity: SurgicalCaseEntity,
  filters: CaseScheduleFilters
): boolean {
  const { startDate, endDate, locationId } = filters;

  if (startDate !== undefined && caseEntity.dateTime < new Date(startDate)) {
    return false;
  }

  if (endDate !== undefined && caseEntity.dateTime > new Date(endDate)) {
    return false;
  }

  if (locationId !== undefined && caseEntity.locationId !== locationId) {
    return false;
  }

  return true;
}

/** Ordered by start time, the way the list is read. */
function byScheduledTime(a: SurgicalCaseEntity, b: SurgicalCaseEntity): number {
  return a.dateTime.getTime() - b.dateTime.getTime();
}

/**
 * The list with this case in its right place — replacing the existing entry,
 * or inserting it if the case has just moved into view.
 */
function withCase(
  data: CasesResponse,
  caseEntity: SurgicalCaseEntity
): CasesResponse {
  const index = data.cases.findIndex((c) => c.id === caseEntity.id);

  if (index === -1) {
    return {
      ...data,
      cases: [...data.cases, caseEntity].sort(byScheduledTime),
      total: data.total + 1,
    };
  }

  const cases = [...data.cases];
  cases[index] = caseEntity;

  return { ...data, cases: cases.sort(byScheduledTime) };
}

/**
 * The list without this case. Returns the original object when the case was
 * not there, so an event about somewhere else costs no re-render.
 */
function withoutCase(data: CasesResponse, caseId: string): CasesResponse {
  const cases = data.cases.filter((c) => c.id !== caseId);

  if (cases.length === data.cases.length) {
    return data;
  }

  return { ...data, cases, total: Math.max(0, data.total - 1) };
}

const hasCasePayload = (
  event: RealtimeEvent
): event is Extract<RealtimeEvent, { data: { case: unknown } }> =>
  typeof event.data === 'object' && event.data !== null && 'case' in event.data;

/**
 * Hook for fetching surgical cases with pagination (no infinite scroll)
 */
export function useCaseSchedulePaginated(
  filters: CaseScheduleFilters = {},
  page = 1,
  pageSize = 20,
  options: UseCaseSchedulePaginatedOptions = {}
) {
  const { enableRealtime = false, enabled = true } = options;
  const queryClient = useQueryClient();
  const queryKey = caseScheduleKeys.list({ ...filters, page, pageSize });

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Extract dates from filters, default to current date if not provided
      let { startDate, endDate } = filters;

      // If no dates provided, use current date (start and end of day)
      if (!startDate || !endDate) {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const day = now.getDate();

        if (!startDate) {
          const start = new Date(year, month, day, 0, 0, 0, 0);
          startDate = start.toISOString();
        }
        if (!endDate) {
          const end = new Date(year, month, day, 23, 59, 59, 999);
          endDate = end.toISOString();
        }
      }

      return caseScheduleRepository.getCases({
        page,
        pageSize,
        startDate,
        endDate,
        locationId: filters.locationId,
        includeChecklistItems: true,
      });
    },
    enabled,
  });

  // Set up real-time updates
  useRealtimeCaseUpdates({
    // Deliberately NOT filtered by date, although the query is.
    //
    // Subscription filters are applied server-side against the event's case,
    // so a case rescheduled off this day would be matched on its *new* date
    // and the event would never reach the day it just left — leaving a stale
    // card there with nothing to remove it. Membership is decided below by
    // `matchesCaseFilters` instead, which sees both sides of the move.
    //
    // `locationId` is absent for a second reason: `CaseEventFilters` has no
    // such field, so scoping to one OR is only ever possible client-side.
    filters: { status: filters.status },
    // Never subscribe for a query that is switched off — there is no cache for
    // the events to patch, so the connection would only burn a subscription.
    enabled: enableRealtime && enabled,
    onEvent: (event: RealtimeEvent) => {
      // Update cache based on event type
      queryClient.setQueryData<CasesResponse>(queryKey, (oldData) => {
        if (!oldData) {
          return oldData;
        }

        // Add defensive check for event.data
        if (!hasCasePayload(event)) {
          console.warn('Received case event with missing data.case:', {
            eventType: event.type,
            timestamp: event.timestamp,
            fullEvent: event,
          });
          return oldData;
        }

        const caseData = event.data.case;
        const caseEntity = new SurgicalCaseEntity(caseData);

        const belongsHere = matchesCaseFilters(caseEntity, filters);

        // A case that does not belong in this query's window must not be in
        // its list — whether it says so outright by being deleted, or by
        // having been rescheduled off this day or moved to another room.
        //
        // Returning `oldData` here instead is what stranded a rescheduled case
        // on the day it had just left: the event arrived, failed the date
        // check, and the stale card was left sitting in the cache.
        if (!belongsHere || event.type === RealtimeEventType.CASE_DELETED) {
          return withoutCase(oldData, caseEntity.id);
        }

        switch (event.type) {
          // Created, or updated onto this day from another one: either way the
          // case belongs here now, so put it where it belongs.
          case RealtimeEventType.CASE_CREATED:
          case RealtimeEventType.CASE_UPDATED: {
            return withCase(oldData, caseEntity);
          }

          // A cancellation restates a case already on the board; it is not a
          // reason to pull one in that was never shown.
          case RealtimeEventType.CASE_CANCELLED: {
            return oldData.cases.some((c) => c.id === caseEntity.id)
              ? withCase(oldData, caseEntity)
              : oldData;
          }

          default:
            return oldData;
        }
      });
    },
  });

  // A case's tray/cart status counts are computed server-side from its
  // tracked assets, so a case-level event alone never fires when one of
  // those assets changes status elsewhere. Scope this to only the case(s)
  // that actually track the changed asset, refetching just that case rather
  // than the whole list.
  useRealtimeAssetUpdates({
    eventTypes: [RealtimeEventType.ASSET_STATUS_UPDATED],
    enabled: enableRealtime,
    onEvent: (event) => {
      if (event.type !== RealtimeEventType.ASSET_STATUS_UPDATED) return;

      const assetId = (event as AssetStatusUpdatedEvent).data.asset.id;
      const cachedData = queryClient.getQueryData<CasesResponse>(queryKey);
      const affectedCase = cachedData?.cases.find((c) =>
        caseTracksAsset(c, assetId)
      );
      if (!affectedCase) return;

      void caseScheduleRepository
        .getCase(affectedCase.id, { includeChecklistItems: true })
        .then((updatedCase) => {
          queryClient.setQueryData<CasesResponse>(queryKey, (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              cases: oldData.cases.map((c) =>
                c.id === updatedCase.id ? updatedCase : c
              ),
            };
          });
        })
        .catch((error: unknown) => {
          console.error(
            'Failed to refresh case after asset status update',
            error
          );
        });
    },
  });

  // Handle visibility changes to catch missed events
  useEffect(() => {
    if (!enableRealtime) {
      return;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible - refresh to catch missed events
        queryClient.invalidateQueries({ queryKey });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enableRealtime, queryKey, queryClient]);

  return query;
}

/**
 * Options for useWorkflowSummary hook
 */
export interface UseWorkflowSummaryOptions {
  /** Whether to enable real-time updates via SSE */
  enableRealtime?: boolean;
}

/**
 * Hook for fetching workflow summary data
 */
export function useWorkflowSummary(
  targetDate: string,
  options: UseWorkflowSummaryOptions = {}
) {
  const { enableRealtime = false } = options;
  const queryClient = useQueryClient();
  const queryKey = caseScheduleKeys.workflowSummary(targetDate);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      return caseScheduleRepository.getWorkflowSummary(targetDate);
    },
    staleTime: 30 * 1000, // 30 seconds
    // Remove refetchInterval - use SSE instead when enabled
    refetchInterval: enableRealtime ? false : undefined,
  });

  // Convert targetDate to date range for filters
  const startDate = targetDate
    ? new Date(targetDate + 'T00:00:00').toISOString()
    : undefined;
  const endDate = targetDate
    ? new Date(targetDate + 'T23:59:59.999').toISOString()
    : undefined;

  // Set up real-time updates
  useRealtimeCaseUpdates({
    filters: {
      startDate,
      endDate,
    },
    enabled: enableRealtime,
    onEvent: () => {
      // Invalidate workflow summary query when any case event occurs
      // This triggers automatic refetch
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Handle visibility changes to catch missed events
  useEffect(() => {
    if (!enableRealtime) {
      return;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible - refresh to catch missed events
        queryClient.invalidateQueries({ queryKey });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enableRealtime, queryKey, queryClient]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Picking stations available for scanning/building a cart, with live status
 * (Available/Busy/Offline). Used when no station is auto-detected via the
 * android bridge.
 */
export function usePickingStations(enabled = true) {
  return useQuery({
    queryKey: caseScheduleKeys.pickingStations(),
    queryFn: () => caseScheduleRepository.getPickingStations(),
    staleTime: 30 * 1000,
    refetchInterval: enabled ? 5 * 1000 : false,
    enabled,
  });
}
